import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import PatientFile from 'model/PatientFile'
import PatientsFilesRepository from 'clients/db/PatientsFilesRepository'
import PatientsFilePage from 'model/PatientsFilePage'
import PatientsFilesPagesRepository from 'clients/db/PatientsFilesPagesRepository'
import PatientsFilesWithPatientsPagesRepository from 'clients/db/PatientsFilesWithPatientsPagesRepository'
import PatientsFilesWithPatientsPage from 'model/PatientsFilesWithPatientsPage'

interface PatientsFilesState {
  isLoading: boolean
  patientFiles: PatientFile[]
  patientsFiles: PatientFile[]
  patientFilesPages: PatientsFilePage[]
  uncategorizedFilesPages: PatientsFilesWithPatientsPage[]
  filter: string | undefined
  file: PatientFile | undefined
}

const initialState: PatientsFilesState = {
  isLoading: false,
  patientFiles: [],
  patientsFiles: [],
  patientFilesPages: [],
  uncategorizedFilesPages: [],
  filter: 'imaging',
  file: undefined,
}

function startLoading(state: PatientsFilesState) {
  state.isLoading = true
}

const patientsFilesSlice = createSlice({
  name: 'patientsFiles',
  initialState,
  reducers: {
    fetchPatientsFilesStart: startLoading,
    fetchPatientsFilesSuccess(state, { payload }: PayloadAction<PatientFile[]>) {
      state.isLoading = false
      state.patientsFiles = payload
    },
    fetchPatientFilesSuccess(state, { payload }: PayloadAction<PatientFile[]>) {
      state.isLoading = false
      state.patientFiles = payload
    },
    fetchUncategorizedFilesFirstPageSuccess: (
      state,
      { payload }: PayloadAction<PatientsFilesWithPatientsPage>,
    ) => {
      state.isLoading = false
      state.uncategorizedFilesPages = [payload]
    },
    fetchUncategorizedFilesPageSuccess: (
      state,
      { payload }: PayloadAction<PatientsFilesWithPatientsPage>,
    ) => {
      state.isLoading = false
      state.uncategorizedFilesPages = [...state.uncategorizedFilesPages, payload]
    },
    fetchPatientsFilesFirstPageSuccess: (state, { payload }: PayloadAction<PatientsFilePage>) => {
      state.isLoading = false
      state.patientFilesPages = [payload]
    },
    fetchPatientsFilesPageSuccess: (state, { payload }: PayloadAction<PatientsFilePage>) => {
      state.isLoading = false
      state.patientFilesPages = [
        ...state.patientFilesPages?.slice(0, parseInt(payload?.bookmark, 10) - 1),
        payload,
        ...state.patientFilesPages?.slice(parseInt(payload?.bookmark, 10) + 1),
      ]
    },
    setQueryFilter: (state, { payload }: PayloadAction<string | undefined>) => {
      state.filter = payload
    },
    fetchPatientFileSuccess: (state, { payload }: PayloadAction<PatientFile>) => {
      state.isLoading = false
      state.file = payload
    },
    clearPatientFile: (state) => {
      state.isLoading = false
      state.file = undefined
    },
  },
})
export const {
  fetchPatientsFilesStart,
  fetchPatientsFilesSuccess,
  fetchPatientFilesSuccess,
  fetchUncategorizedFilesFirstPageSuccess,
  fetchUncategorizedFilesPageSuccess,
  fetchPatientsFilesFirstPageSuccess,
  fetchPatientsFilesPageSuccess,
  setQueryFilter,
  fetchPatientFileSuccess,
  clearPatientFile,
} = patientsFilesSlice.actions

export const fetchPatientFile = (fileId: string): AppThunk => async (dispatch) => {
  dispatch(fetchPatientsFilesStart())
  const file = await PatientsFilesRepository.find(fileId)
  dispatch(fetchPatientFileSuccess(file))
}

export const savePatientFile = (patientFile: PatientFile): AppThunk => async (dispatch) => {
  dispatch(clearPatientFile())
  PatientsFilesRepository.saveOrUpdate(patientFile).then(() => {
    dispatch(fetchPatientFilesPage(patientFile.patientId, '', ''))
    dispatch(clearPatientFile())
  })
}

export const saveUncategorizedFile = (patientFile: PatientFile): AppThunk => async (dispatch) => {
  dispatch(clearPatientFile())
  PatientsFilesRepository.saveOrUpdate(patientFile).then(() => {
    dispatch(fetchUncategorizedFilesPage(''))
  })
}

export const fetchPatientFilesPage = (
  id: string,
  bookmark: string,
  filter: string | undefined,
  limit?: number,
): AppThunk => async (dispatch) => {
  dispatch(fetchPatientsFilesStart())
  const patientsFilesPages = await PatientsFilesPagesRepository.searchPatientsFilePage(
    id,
    bookmark,
    filter,
    limit,
  )
  if (bookmark === '') {
    dispatch(fetchPatientsFilesFirstPageSuccess(patientsFilesPages))
  } else {
    dispatch(fetchPatientsFilesPageSuccess(patientsFilesPages))
  }
}

export const fetchUncategorizedFilesPage = (bookmark: string): AppThunk => async (dispatch) => {
  dispatch(fetchPatientsFilesStart())
  let patientsFilesPages
  patientsFilesPages = await PatientsFilesWithPatientsPagesRepository.searchUnCategorizedPage(
    bookmark,
  )
  if (bookmark === '') {
    dispatch(fetchUncategorizedFilesFirstPageSuccess(patientsFilesPages))
  } else {
    dispatch(fetchUncategorizedFilesPageSuccess(patientsFilesPages))
  }
}

export default patientsFilesSlice.reducer
